import React, { Fragment, useState, useEffect } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { Icon, Intent } from "@blueprintjs/core";
import { Link } from "react-router-dom";
import style from "./index.module.scss";
import CommonSelectField from "../../Components/CommonFields/CommonSelectField";
import CommonSwitch from "../../Components/CommonFields/CommonSwitch";
import { POST, PUT, TenantID, GET } from "../dataSaver";
import { ErrorToaster, SuccessToaster } from "../../utils/toaster";
import EmailTemplates from "../../Components/EmailTemplate/emailTemplates";
import { Auth, GetEntityDetails, currentUser } from "../../utils/auth";
import AddSiteSelectionPopup from "./AddSiteSelectionPopup";
import axios from "axios";
import { fetchEntityDetails } from "../../utils/generalConfig";

const TimesheetAlerts = ({
  edit,
  alertData,
  setOpenAlert,
  selectedSite,
  reminderType: propReminderType,
  inModal = false,
}) => {
  const { siteId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get reminderType with proper priority
  const reminderTypeFromState = location.state?.reminderType;
  const reminderType =
    propReminderType ||
    reminderTypeFromState ||
    searchParams.get("reminderType") ||
    "TIMESHEET_CREATION_REMINDER";

  // No need to get selectedSite from location as it's now passed as a prop
  // const selectedSite = location.state?.selectedSite;

  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [entityData, setEntityData] = useState();
  const [reminderData, setReminderData] = useState(null);
  const [lastModifiedDate, setLastModifiedDate] = useState(null);
  const [reminders, setReminders] = useState([
    { id: 1, isFirst: true, days: 1, enabled: true },
  ]);
  const [alertDetails, setAlertDetails] = useState({
    hours: 1,
    enabled: true,
  });
  const [expirationNotificationEnabled, setExpirationNotificationEnabled] =
    useState(true);
  const [expirationNotificationTemplate, setExpirationNotificationTemplate] =
    useState({
      templateBody: "Default template",
      customTemplate: false,
    });
  const isMultiSiteEntity =
    sessionStorage.getItem("isMultiSiteEntity") === "true";
  const [isUpdated, setIsUpdated] = useState(false);
  const [data, setReminderConfig] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [showMailTemplate, setShowMailTemplate] = useState(false);
  const loggedInUser = currentUser();
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [reminderNotificationAvaliable, setReminderNotificationAvaliable] =
    useState(true);
  const [emailTemplateData, setEmailTemplateData] = useState({
    isOpen: false,
    type: "",
    reminderIndex: null,
  });

  useEffect(() => {
    // If no selected site and multisite is true, redirect back
    if (isMultiSiteEntity && !selectedSite) {
      if (inModal) {
        setOpenAlert(false);
      } else {
        navigate("/alerts-notifications");
      }
      return;
    }

    console.log("TimesheetAlerts useEffect triggered with:", {
      selectedSite,
      edit,
      alertData,
      reminderType,
      isMultiSiteEntity,
    });

    getEntityData();
    // Mark as updated when component mounts
    setIsUpdated(true);
  }, [selectedSite]);

  const getIsExpanded = (value) => {
    setIsExpanded(value);
  };

  const addReminder = () => {
    // Get the last reminder's days value
    const lastReminder = reminders[reminders.length - 1];
    const lastDays = Number(lastReminder.days);

    // Check if last reminder already has maximum days (15)
    if (lastDays >= 15) {
      ErrorToaster("Maximum days are selected. We can't add more reminders.");
      return;
    }

    // Set new reminder's days to lastDays + 1
    const newReminder = {
      id: reminders.length + 1,
      isFirst: false,
      days: lastDays + 1,
      enabled: true,
    };
    setReminders([...reminders, newReminder]);
  };

  const deleteReminder = (id) => {
    setReminders(reminders.filter((reminder) => reminder.id !== id));
  };

  const handleSwitchChange = (id, event) => {
    const value = event.target.checked;
    console.log("Switch value changed:", value);

    if (reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER") {
      console.log("Setting reminderNotificationAvaliable to:", value);
      setReminderNotificationAvaliable(value);
      setReminders((prevReminders) => [
        {
          ...prevReminders[0],
          enabled: value,
        },
      ]);
    } else {
      setReminders((prevReminders) =>
        prevReminders.map((reminder) =>
          reminder.id === id
            ? {
                ...reminder,
                enabled: value,
                days: value ? reminder.days : "",
              }
            : reminder
        )
      );
    }
    setIsUpdated(true);
  };

  const handleAlertSwitchChange = (event) => {
    const value = event.target.checked;
    console.log("Alert switch value changed:", value);

    setAlertDetails((prev) => ({
      ...prev,
      enabled: value,
      hours: value ? prev.hours : "",
    }));
    setIsUpdated(true);
  };

  const handleExpirationSwitchChange = (event) => {
    const value = event.target.checked;
    console.log("Expiration switch value changed:", value);
    setExpirationNotificationEnabled(value);
    // No additional data to reset for expiration notification
    setIsUpdated(true);
  };

  const getOrdinalNumber = (num) => {
    const ordinals = [
      "First",
      "Second",
      "Third",
      "Fourth",
      "Fifth",
      "Sixth",
      "Seventh",
      "Eighth",
      "Ninth",
      "Tenth",
      "Eleventh",
      "Twelfth",
      "Thirteenth",
      "Fourteenth",
      "Fifteenth",
    ];
    return ordinals[num - 1] || `${num}th`;
  };
  const getReminderData = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching data for reminderType:", reminderType);

      // Determine if we're in create mode or edit mode
      const isCreateMode = !edit && !alertData?.id;
      console.log("Is create mode:", isCreateMode);

      // If we're in create mode, set default values and return early
      if (isCreateMode) {
        console.log("Setting default values for new reminder");
        setDefaultValues();
        setLastModifiedDate(null);
        setIsLoading(false);
        return;
      }

      // Construct API URL with explicit reminderType
      let apiUrl = `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&offset=0&limit=100`;

      if (isMultiSiteEntity && selectedSite?.id) {
        apiUrl += `&siteId=${selectedSite.id}`;
      }

      if (edit && alertData?.id) {
        apiUrl += `&id=${alertData.id}`;
      }

      console.log("API URL:", apiUrl);

      // Fetch data from API
      const { data } = await GET(apiUrl);
      console.log("API Response:", data);

      if (data && data.reminderData && data.reminderData.length > 0) {
        const reminderConfig = data.reminderData[0];
        setReminderConfig(reminderConfig);
        setIsEdit(edit);
        setIsUpdated(true);

        // Store the last modified date
        if (reminderConfig.lastModifiedDate) {
          setLastModifiedDate(reminderConfig.lastModifiedDate);
        }

        if (reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER") {
          // Get the notification state from API response
          const notificationAvailable =
            reminderConfig.reminderNotificationAvaliable ?? true;
          console.log(
            "API Response - reminderNotificationAvaliable:",
            notificationAvailable
          );
          setReminderNotificationAvaliable(notificationAvailable);

          // Set reminders with the same state
          setReminders([
            {
              id: 1,
              isFirst: true,
              days: 0,
              enabled: notificationAvailable,
              templateBody:
                reminderConfig.approverNotificationDetails?.templateBody ??
                "Default template",
              customTemplate:
                reminderConfig.approverNotificationDetails?.customTemplate ??
                false,
            },
          ]);

          // Set review reminder details
          setAlertDetails({
            hours:
              reminderConfig.reviewReminderDetails?.reminderSchedule?.days || 1,
            enabled:
              reminderConfig.reviewReminderDetails?.reminderAvailable || false,
            templateBody:
              reminderConfig.reviewReminderDetails?.templateBody ||
              "Default template",
            customTemplate:
              reminderConfig.reviewReminderDetails?.customTemplate || false,
          });

          // Set past due notification details
          setExpirationNotificationEnabled(
            reminderConfig.pastDueNotificationDetails?.reminderAvailable ||
              false
          );
          setExpirationNotificationTemplate({
            templateBody:
              reminderConfig.pastDueNotificationDetails?.templateBody ||
              "Default template",
            customTemplate:
              reminderConfig.pastDueNotificationDetails?.customTemplate ||
              false,
          });
        } else {
          // Handle other reminder types as before
          if (
            reminderConfig.reminderDetails &&
            reminderConfig.reminderDetails.length > 0
          ) {
            const transformedReminders = reminderConfig.reminderDetails.map(
              (reminder, index) => ({
                id: index + 1,
                isFirst: index === 0,
                days: reminder.reminderSchedule.days,
                enabled: reminder.reminderAvailable,
                templateBody: reminder.templateBody,
              })
            );
            setReminders(transformedReminders);
          } else {
            setReminders([
              {
                id: 1,
                isFirst: true,
                days: 1,
                enabled: true,
                templateBody: "Default template",
              },
            ]);
          }

          setAlertDetails({
            hours: reminderConfig.alertDetails?.reminderSchedule?.days || 1,
            enabled: reminderConfig.alertDetails?.reminderAvailable || false,
            templateBody:
              reminderConfig.alertDetails?.templateBody || "Default template",
            customTemplate:
              reminderConfig.alertDetails?.customTemplate || false,
          });
          setExpirationNotificationEnabled(
            reminderConfig.expirationNotificationDetails?.reminderAvailable ||
              false
          );

          // Initialize expiration notification template
          setExpirationNotificationTemplate({
            templateBody:
              reminderConfig.expirationNotificationDetails?.templateBody ||
              "Default template",
            customTemplate:
              reminderConfig.expirationNotificationDetails?.customTemplate ||
              false,
          });
        }
      } else {
        // Set default values
        setDefaultValues();
        setLastModifiedDate(null);
      }
    } catch (error) {
      console.error("Error in getReminderData:", error);
      ErrorToaster("Error loading reminder data");
    } finally {
      setIsLoading(false);
    }
  };

  const setDefaultValues = () => {
    // Set default reminders
    if (reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER") {
      setReminders([
        {
          id: 1,
          isFirst: true,
          days: 0,
          enabled: true,
          templateBody: "Default template",
          customTemplate: false,
        },
      ]);
      setReminderNotificationAvaliable(true);
    } else {
      setReminders([
        {
          id: 1,
          isFirst: true,
          days: "",
          enabled: true,
          templateBody: "Default template",
          customTemplate: false,
        },
      ]);
    }

    // Set default alert details
    setAlertDetails({
      hours: "",
      enabled: true,
      templateBody: "Default template",
      customTemplate: false,
    });

    // Set default expiration notification
    setExpirationNotificationEnabled(true);

    // Set default expiration notification template
    setExpirationNotificationTemplate({
      templateBody: "Default template",
      customTemplate: false,
    });

    // Reset any other state that might be carrying over
    setReminderConfig(null);
    setIsEdit(false);
    setIsUpdated(true);
  };

  const getEntityData = async () => {
    try {
      const entity = await fetchEntityDetails({ tenantId: TenantID });
      setEntityData(entity);
      console.log("Entity Data Sites:", entity?.sites);
      console.log("Selected Site:", selectedSite);

      // For non-multiSite, get reminder data with the first site
      if (!isMultiSiteEntity && entity?.sites?.length > 0) {
        // First check if there's existing data for this reminderType
        const checkUrl = `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&siteId=${entity.sites[0].id}&offset=0&limit=100`;
        const { data: existingData } = await GET(checkUrl);

        if (existingData?.reminderData?.length > 0) {
          // If data exists, set it
          const reminderConfig = existingData.reminderData[0];
          setReminderConfig(reminderConfig);
          setIsEdit(true);
          setIsUpdated(true);

          // Set last modified date
          if (reminderConfig.lastModifiedDate) {
            setLastModifiedDate(reminderConfig.lastModifiedDate);
          }

          // Set the data based on reminder type
          if (reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER") {
            setReminderNotificationAvaliable(
              reminderConfig.reminderNotificationAvaliable ?? true
            );
            setReminders([
              {
                id: 1,
                isFirst: true,
                days: 0,
                enabled:
                  reminderConfig.approverNotificationDetails
                    ?.reminderNotificationAvaliable ?? true,
                templateBody:
                  reminderConfig.approverNotificationDetails?.templateBody ??
                  "Default template",
                customTemplate:
                  reminderConfig.approverNotificationDetails?.customTemplate ??
                  false,
              },
            ]);

            setAlertDetails({
              hours:
                reminderConfig.reviewReminderDetails?.reminderSchedule?.days ||
                1,
              enabled:
                reminderConfig.reviewReminderDetails?.reminderAvailable ||
                false,
              templateBody:
                reminderConfig.reviewReminderDetails?.templateBody ||
                "Default template",
              customTemplate:
                reminderConfig.reviewReminderDetails?.customTemplate || false,
            });

            setExpirationNotificationEnabled(
              reminderConfig.pastDueNotificationDetails?.reminderAvailable ||
                false
            );
          } else {
            if (reminderConfig.reminderDetails?.length > 0) {
              const transformedReminders = reminderConfig.reminderDetails.map(
                (reminder, index) => ({
                  id: index + 1,
                  isFirst: index === 0,
                  days: reminder.reminderSchedule.days,
                  enabled: reminder.reminderAvailable,
                  templateBody: reminder.templateBody,
                })
              );
              setReminders(transformedReminders);
            }

            setAlertDetails({
              hours: reminderConfig.alertDetails?.reminderSchedule?.days || 1,
              enabled: reminderConfig.alertDetails?.reminderAvailable || false,
              templateBody:
                reminderConfig.alertDetails?.templateBody || "Default template",
              customTemplate:
                reminderConfig.alertDetails?.customTemplate || false,
            });

            setExpirationNotificationEnabled(
              reminderConfig.expirationNotificationDetails?.reminderAvailable ||
                false
            );

            setExpirationNotificationTemplate({
              templateBody:
                reminderConfig.expirationNotificationDetails?.templateBody ||
                "Default template",
              customTemplate:
                reminderConfig.expirationNotificationDetails?.customTemplate ||
                false,
            });
          }
        } else {
          // If no existing data, set defaults
          setDefaultValues();
          setLastModifiedDate(null);
        }
      } else if (isMultiSiteEntity) {
        // For multiSite, get reminder data normally
        getReminderData();
      }
    } catch (error) {
      console.error("Error fetching entity data:", error);
      ErrorToaster("Error fetching entity data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Only validate reminder days for non-review/approve reminder types
      if (reminderType !== "TIMESHEET_REVIEW_APPROVE_REMINDER") {
        const enabledRemindersWithoutDays = reminders.filter(
          (reminder) =>
            reminder.enabled && (!reminder.days || reminder.days === "")
        );

        if (enabledRemindersWithoutDays.length > 0) {
          ErrorToaster("Please select days for all enabled reminders");
          return;
        }
      }

      // Validate that alert has hours selected if enabled
      if (
        alertDetails.enabled &&
        (!alertDetails.hours || alertDetails.hours === "")
      ) {
        ErrorToaster("Please select hours for enabled alert");
        return;
      }

      // For TIMESHEET_REVIEW_APPROVE_REMINDER, only validate if the respective toggles are enabled
      if (reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER") {
        // Only validate review reminder details if it's enabled
        if (
          alertDetails.enabled &&
          (!alertDetails.hours || alertDetails.hours === "")
        ) {
          ErrorToaster("Please select hours for enabled review reminder");
          return;
        }

        // Skip validation for disabled toggles
        if (
          !reminderNotificationAvaliable &&
          !alertDetails.enabled &&
          !expirationNotificationEnabled
        ) {
          // All toggles are off, no validation needed
          // Proceed with the save
        }
      }

      await saveData();

      // Always show confirmation dialog for both multisite and single site
      setShowSaveConfirmation(true);
    } catch (error) {
      ErrorToaster("Error saving data");
    }
  };

  const saveData = async () => {
    try {
      // Log the current state before saving
      console.log("Saving with reminderType:", reminderType);
      console.log("Selected Site:", selectedSite);
      console.log("Existing Data:", data);
      console.log("Is Multi Site Entity:", isMultiSiteEntity);

      let siteData = null;

      if (isMultiSiteEntity) {
        // For multi-site, use the selected site
        siteData = selectedSite?.selectedSite
          ? {
              id: String(selectedSite.selectedSite.id),
              name: selectedSite.selectedSite.name,
            }
          : selectedSite
          ? {
              id: String(selectedSite.id),
              name: selectedSite.name,
            }
          : null;
      } else {
        // For single site, use the first site from entityData
        siteData = entityData?.sites?.[0]
          ? {
              id: String(entityData.sites[0].id),
              name: entityData.sites[0].siteName?.siteName,
            }
          : null;
      }

      const requestData = {
        tenant: {
          id: String(TenantID),
        },
        site: siteData,
        noReminders:
          reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
            ? 0
            : reminders.length,
        ...(reminderType !== "TIMESHEET_REVIEW_APPROVE_REMINDER" && {
          reminderDetails: reminders.map((reminder) => ({
            reminderSchedule: {
              days: Number(reminder.days),
              reminderTiming: "AFTER",
            },
            templateBody: reminder.templateBody || "Default template",
            shouldExcludeHolidays: false,
            customTemplate: reminder.customTemplate || false,
            reminderAvailable: Boolean(reminder.enabled),
          })),
        }),
        ...(reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
          ? {
              approverNotificationDetails: {
                templateBody: reminders[0]?.templateBody || "Default template",
                reminderNotificationAvaliable: reminderNotificationAvaliable,
                customTemplate: reminders[0]?.customTemplate || false,
              },
              reviewReminderDetails: {
                reminderSchedule: {
                  days: Number(alertDetails.hours),
                  reminderTiming: "AFTER",
                },
                templateBody: alertDetails.templateBody || "Default template",
                shouldExcludeHolidays: false,
                customTemplate: alertDetails.customTemplate || false,
                reminderAvailable: Boolean(alertDetails.enabled),
              },
              pastDueNotificationDetails: {
                reminderSchedule: {
                  days: 0,
                  reminderTiming: "BEFORE",
                },
                templateBody:
                  data?.reminderData?.[0]?.pastDueNotificationDetails
                    ?.templateBody || "Default template",
                shouldExcludeHolidays: false,
                reminderAvailable: Boolean(expirationNotificationEnabled),
                customTemplate:
                  data?.reminderData?.[0]?.pastDueNotificationDetails
                    ?.customTemplate || false,
              },
            }
          : {
              alertDetails: {
                reminderSchedule: {
                  days: Number(alertDetails.hours),
                  reminderTiming: "AFTER",
                },
                templateBody: alertDetails.templateBody || "Default template",
                shouldExcludeHolidays: false,
                customTemplate: alertDetails.customTemplate || false,
                reminderAvailable: Boolean(alertDetails.enabled),
              },
              expirationNotificationDetails: {
                reminderSchedule: {
                  days: 0,
                  reminderTiming: "BEFORE",
                },
                templateBody:
                  expirationNotificationTemplate.templateBody ||
                  "Default template",
                shouldExcludeHolidays: false,
                customTemplate:
                  expirationNotificationTemplate.customTemplate || false,
                reminderAvailable: Boolean(expirationNotificationEnabled),
              },
            }),
        lastUpdatedUser: {
          id: String(loggedInUser?.id || ""),
          title: {
            title: String(loggedInUser?.title || ""),
            id: String(loggedInUser?.titleId || ""),
          },
          name: {
            name: String(
              `${loggedInUser?.firstName || ""} ${loggedInUser?.lastName || ""}`
            ).trim(),
            firstName: String(loggedInUser?.firstName || ""),
            lastName: String(loggedInUser?.lastName || ""),
            middleName: String(loggedInUser?.middleName || ""),
            suffix: {
              id: String(loggedInUser?.suffixId || ""),
              suffix: String(loggedInUser?.suffix || ""),
            },
          },
        },
      };

      // Log the request data to verify reminderDetails
      console.log("Request Data:", requestData);

      let response;
      if (data?.id || (edit && alertData?.id)) {
        const reminderId = data?.id || alertData?.id;
        response = await PUT(
          `timesheet-management-service/reminder/${reminderType}/${reminderId}`,
          requestData
        );
        SuccessToaster("Reminder Updated Successfully");
        if (!isMultiSiteEntity && setOpenAlert) {
          setOpenAlert(false);
        }
      } else {
        response = await POST(
          `timesheet-management-service/reminder/${reminderType}`,
          requestData
        );
        SuccessToaster("Reminder Created Successfully");
      }
      await getReminderData();
      return response;
    } catch (error) {
      console.error("Error saving data:", error);
      console.error("Error details:", error.response?.data);
      ErrorToaster(
        error.response?.data?.message || "Error saving reminder configuration"
      );
      throw error;
    }
  };

  const handleReminderDaysChange = (id, value) => {
    setReminders((prevReminders) => {
      // Find the index of the current reminder
      const currentIndex = prevReminders.findIndex((r) => r.id === id);

      // Filter out reminders that come after the current one and have days less than or equal to the new value
      const updatedReminders = prevReminders.filter((reminder, index) => {
        if (reminder.id === id) {
          return true; // Keep the current reminder
        }
        if (index > currentIndex && reminder.days <= value) {
          return false; // Remove subsequent reminders with days <= new value
        }
        return true; // Keep all other reminders
      });

      // Update the current reminder's days
      return updatedReminders.map((reminder) =>
        reminder.id === id ? { ...reminder, days: value } : reminder
      );
    });
    setIsUpdated(true);
  };

  const handleAlertHoursChange = (value) => {
    setAlertDetails((prev) => ({
      ...prev,
      hours: value,
    }));
    setIsUpdated(true); // Mark as updated when changes occur
  };

  // Create arrays for dropdown options with dynamic disabled state
  const getDaysOptions = (reminderId) => {
    const previousReminder = reminders.find((r) => r.id === reminderId - 1);
    const nextReminder = reminders.find((r) => r.id === reminderId + 1);

    return Array.from({ length: 15 }, (_, i) => {
      const dayValue = i + 1;
      const isDisabled = previousReminder
        ? dayValue <= previousReminder.days
        : false;
      return {
        value: dayValue,
        label: `${dayValue} ${dayValue === 1 ? "Day" : "Days"}`,
        disabled: isDisabled,
      };
    });
  };

  const hoursOptions = [
    { value: 1, label: "24 hours" },
    { value: 2, label: "48 hours" },
    { value: 3, label: "72 hours" },
    { value: 4, label: "96 hours" },
    { value: 5, label: "120 hours" }
  ];
  const [templateName, setTemplateName] = useState(null);

  const handleViewTemplate = (type, index = null) => {
    // Get the current template data based on type
    let currentTemplateBody = "";
    let isCustomTemplate = false;
    setTemplateName(type);
    if (type === "reminder" && reminders[index]) {
      currentTemplateBody = reminders[index].templateBody;
      isCustomTemplate = reminders[index].customTemplate || false;
    } else if (type === "alert") {
      // For alert templates, directly use the alertDetails.templateBody
      currentTemplateBody = alertDetails.templateBody;
      isCustomTemplate = alertDetails.customTemplate || false;

      // Log for debugging
      console.log("Opening alert template:", {
        templateBody: currentTemplateBody,
        isCustomTemplate: isCustomTemplate,
        alertDetails: alertDetails,
      });
    } else if (type === "expiration") {
      // For expiration templates, directly use the expirationNotificationTemplate.templateBody
      currentTemplateBody = expirationNotificationTemplate.templateBody;
      isCustomTemplate = expirationNotificationTemplate.customTemplate || false;

      // Log for debugging
      console.log("Opening expiration template:", {
        templateBody: currentTemplateBody,
        isCustomTemplate: isCustomTemplate,
        expirationNotificationTemplate: expirationNotificationTemplate,
      });
    } else if (type === "pastdue") {
      currentTemplateBody =
        data?.reminderData?.[0]?.pastDueNotificationDetails?.templateBody;
      isCustomTemplate =
        data?.reminderData?.[0]?.pastDueNotificationDetails?.customTemplate ||
        false;
    }

    setEmailTemplateData({
      isOpen: true,
      type: type,
      reminderIndex: index,
      currentTemplateBody: currentTemplateBody || null, // Pass null to use default template
      isCustomTemplate: isCustomTemplate,
    });
  };

  const handleTemplateClose = () => {
    setEmailTemplateData((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const handleTemplateSave = (templateData) => {
    const { templateBody, customTemplate } = templateData;

    // Update the reminder's templateBody based on the template type and index
    if (emailTemplateData.type === "reminder") {
      if (reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER") {
        // For review/approve type, update approverNotificationDetails
        setReminders((prevReminders) => [
          {
            ...prevReminders[0],
            templateBody: templateBody,
            customTemplate: customTemplate,
          },
        ]);
      } else {
        // For regular reminders
        setReminders((prevReminders) => {
          const updatedReminders = [...prevReminders];
          if (updatedReminders[emailTemplateData.reminderIndex]) {
            updatedReminders[emailTemplateData.reminderIndex] = {
              ...updatedReminders[emailTemplateData.reminderIndex],
              templateBody: templateBody,
              customTemplate: customTemplate,
            };
          }
          return updatedReminders;
        });
      }
    } else if (emailTemplateData.type === "alert") {
      // For alert templates
      setAlertDetails((prev) => ({
        ...prev,
        templateBody: templateBody,
        customTemplate: customTemplate,
      }));
    } else if (emailTemplateData.type === "expiration") {
      // For expiration notification templates
      setExpirationNotificationTemplate({
        templateBody: templateBody,
        customTemplate: customTemplate,
      });
    } else if (emailTemplateData.type === "pastdue") {
      // For past due notification templates
      if (data && data.reminderData && data.reminderData.length > 0) {
        const updatedData = { ...data };
        if (!updatedData.reminderData[0].pastDueNotificationDetails) {
          updatedData.reminderData[0].pastDueNotificationDetails = {};
        }
        updatedData.reminderData[0].pastDueNotificationDetails.templateBody =
          templateBody;
        updatedData.reminderData[0].pastDueNotificationDetails.customTemplate =
          customTemplate;
        setReminderConfig(updatedData);
      }
    }

    // Mark as updated to enable save button
    setIsUpdated(true);
  };

  // Add a function to get the heading based on reminderType
  const getHeading = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "TIMESHEET CREATION ALERTS";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "CREATED TIMESHEET SUBMISSION REMINDERS";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "TIMESHEET / INVOICE REVIEW & APPROVAL ALERTS";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "TIMESHEET REJECTION / DISPUTE ALERTS";
      default:
        return "TIMESHEET ALERTS";
    }
  };
  const getSetupAlertHeader = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "Setup reminders notifications for Activity Loggers to create their Timesheet(s) following the last day of the Timesheet period indicated in their contract.";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "Setup reminders notifications for Activity Loggers to submit their created Timesheet(s) prior to the Timesheet submission date indicated in their contract.";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "Setup reminder notifications for Reviewers & Approvers to be alerted when new Timesheet(s) / Invoice(s) are assigned to them for review..";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "Setup reminders notifications for Activity Loggers to Resolve their Rejected / Disputed Timesheet(s) prior to the Timesheet Resubmission date indicated in their contract..";
      default:
        return "Timesheet Period Expiration Alert";
    }
  };

  const getExpirationAlertHeader = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "Timesheet Creation Period Expiration Alert";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "Timesheet Submission Deadline Alert";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "Reminder to Review Timesheet(s) / Invoice(s) Prior to the respective Review Due Date";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "Timesheet Resolution Period Expiration Alert";
      default:
        return "Timesheet Period Expiration Alert";
    }
  };

  const getAlertDescription = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "Timesheet Creation Period Expiration Notification.";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "Timesheet Submission Period Expiration Notification.";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "Past due Alert Notification for Reviewing assigned Timesheet(s) / Invoice(s).";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "Timesheet Resolution Period Expiration Notification.";
      default:
        return "Timesheet Period Expiration Alert";
    }
  };

  // Add this helper function to format the date
  const formatUpdatedTime = (date) => {
    if (!date) return "";
    const options = {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/New_York",
    };
    return (
      date.toLocaleString("en-US", options).replace(",", "").toUpperCase() +
      " EST"
    );
  };

  // Get the updated time based on reminder type
  const getUpdatedTime = () => {
    // First priority: use the last modified date from API response
    if (lastModifiedDate) {
      return `UPDATED ON ${formatUpdatedTime(new Date(lastModifiedDate))}`;
    }
    // Second priority: use the alertData if in edit mode
    else if (edit && alertData && alertData.lastUpdatedDate) {
      return `UPDATED ON ${formatUpdatedTime(
        new Date(alertData.lastUpdatedDate)
      )}`;
    }
    return null; // Return null for new reminders or when no date available
  };

  // Add getReminderTypeText function
  const getReminderTypeText = (type) => {
    switch (type) {
      case "TIMESHEET_CREATION_REMINDER":
        return "Timesheet Creation";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "Timesheet Submission";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "Timesheet / Invoice Review & Approval";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "Timesheet Rejection / Dispute";
      default:
        return "Timesheet Creation";
    }
  };

  // Add getButtonText function before the return statement
  const getButtonText = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "CREATE & SUBMIT TIMESHEET";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "SUBMIT TIMESHEET";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "REVIEW TIMESHEET    ";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "RESOLVE TIMESHEET";
      default:
        return "CREATE & SUBMIT TIMESHEET";
    }
  };

  // Add getTemplateHeaderData function before handleViewTemplate
  const getTemplateHeaderData = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return {
          name: "{Name}",
          timesheetName: "{Timesheet Name}",
          startDate: "{Start Date}",
          endDate: "{End Date}",
          contractName: "{Contract Name}",
          contractId: "{Contract ID}",
          days: "{Days}",
        };
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return {
          name: "{Name}",
          timesheetName: "{Timesheet Name}",
          startDate: "{Start Date}",
          endDate: "{End Date}",
          contractName: "{Contract Name}",
          contractId: "{Contract ID}",
          days: "{Days}",
        };
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return {
          name: "{Name}",
          timesheetName: "{Timesheet Name}",
          startDate: "{Start Date}",
          endDate: "{End Date}",
          contractName: "{Contract Name}",
          contractId: "{Contract ID}",
          days: "{Days}",
        };
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return {
          name: "{Name}",
          timesheetName: "{Timesheet Name}",
          startDate: "{Start Date}",
          endDate: "{End Date}",
          contractName: "{Contract Name}",
          contractId: "{Contract ID}",
          days: "{Days}",
        };
      default:
        return {
          name: "{Name}",
          timesheetName: "Activity Logger Timesheet",
          startDate: "{Start Date}",
          endDate: "{End Date}",
          contractName: "{Contract Name}",
          contractId: "{Contract ID}",
          days: "{Days}",
        };
    }
  };

  // Add this function before the return statement
  const getToEmail = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "Activity Logger / Practice Manager"; // Email for activity loggers
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "Activity Logger / Practice Manager"; // Email for activity loggers
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "Reviewer / Approver"; // Email for reviewers
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "Activity Logger / Practice Manager"; // Email for activity loggers
      default:
        return "user@example.com";
    }
  };

  // Add this function before the return statement
  const getSubject = () => {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "[Timesheet Name] - [Period]: Timesheet Creation Reminder";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "[Timesheet Name] - [Period]: Submission Reminder";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "[Timesheet Name] - [Period]: Review & Approval Reminder";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "[Timesheet Name] - [Period]: Rejection/Dispute Reminder";
      default:
        return "[Timesheet Name] - [Period]: Reminder";
    }
  };

  return (
    <div className={style.contentWrapper}>
      {/* Display selected site info */}
      {selectedSite?.selectedSite ? (
        <div
          className={`${style.setupDescription} ${style.paddingTop20} ${style.paddingBottom20} ${style.paddingLeft20}`}
        >
          <div className={style.siteSelectionWrapper}>
            <div className={style.siteSelectionHeader}>
              Selected Site To Setup {getReminderTypeText(reminderType)}{" "}
              Notification Alerts
            </div>
            <div className={style.siteInfo}>
              {selectedSite?.selectedSite?.name}
            </div>
          </div>
        </div>
      ) : selectedSite?.name ? (
        <div
          className={`${style.setupDescription} ${style.paddingTop20} ${style.paddingBottom20} ${style.paddingLeft20}`}
        >
          <div className={style.siteSelectionWrapper}>
            <div className={style.siteSelectionHeader}>
              Selected Site To Setup {getReminderTypeText(reminderType)}{" "}
              Notification Alerts
            </div>
            <div className={style.siteInfo}>{selectedSite.name}</div>
          </div>
        </div>
      ) : null}

      <div
        className={`${style.siteSelectionHeader} ${style.paddingLeft20}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "10px",
        }}
      >
        <span style={{ textAlign: "start" }} className="remainderText">
          {getSetupAlertHeader()}
        </span>
        {reminderType !== "TIMESHEET_REVIEW_APPROVE_REMINDER" && (
          <button className={style.addReminder} onClick={addReminder}>
            <Icon icon="add" /> ADD REMINDER
          </button>
        )}
      </div>

      {reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER" ? (
        <div className={style.alertSection}>
          <div className={style.alertRow}>
            <div className={style.labelCol}>Enable Alert</div>
            <CommonSwitch
              checked={reminderNotificationAvaliable}
              label={reminderNotificationAvaliable ? "Yes" : "No"}
              onChange={(event) => handleSwitchChange(null, event)}
            />
          </div>
          {/* {reminderNotificationAvaliable && (
            <div className={style.subRow}>
              <div className={style.label}>Email Notification</div>
              <div className={style.templateLink}>
                <span>Default Template In Use</span>
                <button
                  className={style.viewTemplate}
                  onClick={() => handleViewTemplate("reminder", 0)}
                >
                  VIEW TEMPLATE
                </button>
              </div>
            </div>
          )} */}
        </div>
      ) : (
        reminders.map((reminder, index) => (
          <div key={reminder.id} className={style.alertSection}>
            <div className={style.alertRow}>
              <div className={style.labelCol}>
                {reminder.isFirst
                  ? "First Reminder"
                  : `${getOrdinalNumber(index + 1)} Reminder`}
              </div>
              <CommonSwitch
                checked={reminder.enabled}
                label={reminder.enabled ? "Yes" : "No"}
                onChange={(event) => handleSwitchChange(reminder.id, event)}
              />
              <div className={style.valueCol}></div>

              {reminder.enabled && (
                <>
                  <div className={style.selectWrapper}>
                    <CommonSelectField
                      value={reminder.days}
                      onChange={(e) =>
                        handleReminderDaysChange(reminder.id, e.target.value)
                      }
                      valueList={getDaysOptions(reminder.id).map(
                        (opt) => opt.value
                      )}
                      labelList={getDaysOptions(reminder.id).map(
                        (opt) => opt.label
                      )}
                      disabledList={getDaysOptions(reminder.id).map(
                        (opt) => opt.disabled
                      )}
                      firstOptionLabel="Select Days"
                      firstOptionValue=""
                    />
                  </div>
                  <div
                    className={`${style.periodText} ${style.marginRight100}`}
                  >
                    After Each Timesheet Period End Date
                  </div>
                </>
              )}
            </div>
            {!reminder.isFirst && (
              <div className={style.deleteReminderWrapper}>
                <button
                  className={style.deleteReminder}
                  onClick={() => deleteReminder(reminder.id)}
                >
                  <Icon icon="trash" intent={Intent.DANGER} />
                  <span>Delete Reminder</span>
                </button>
              </div>
            )}
            {reminder.enabled && (
              <div className={style.subRow}>
                <div className={style.label}>Email Notification</div>
                <div className={style.templateLink}>
                  <span>Default Template In Use</span>
                  <button
                    className={style.viewTemplate}
                    onClick={() => handleViewTemplate("reminder", index)}
                  >
                    VIEW TEMPLATE
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}

      <div className={style.alertSection}>
        <div className={`${style.siteSelectionHeader} ${style.alignHeader}`}>
          {getExpirationAlertHeader()}
        </div>
        <div className={style.alertRow}>
          <div className={style.labelCol}>
            {" "}
            {reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
              ? "Enable Alert"
              : "Alert"}
          </div>
          <CommonSwitch
            checked={alertDetails.enabled}
            label={alertDetails.enabled ? "Yes" : "No"}
            onChange={handleAlertSwitchChange}
          />
          <div className={style.valueCol}></div>

          {alertDetails.enabled && (
            <>
              <div className={style.selectWrapper}>
                <CommonSelectField
                  value={alertDetails.hours}
                  onChange={(e) => handleAlertHoursChange(e.target.value)}
                  valueList={hoursOptions.map((opt) => opt.value)}
                  labelList={hoursOptions.map((opt) => opt.label)}
                  disabledList={new Array(15).fill(false)}
                  firstOptionLabel="Select Hours"
                  firstOptionValue=""
                />
              </div>
              <div className={style.periodText}>
                Prior To Each Timesheet Creation Period Expiration Date
              </div>
            </>
          )}
        </div>
        {alertDetails.enabled && (
          <div className={style.subRow}>
            <div className={style.label}>Email Alert</div>
            <div className={style.templateLink}>
              <span>Default Template In Use</span>
              <button
                className={style.viewTemplate}
                onClick={() => handleViewTemplate("alert")}
              >
                VIEW TEMPLATE
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={style.alertSection}>
        <div className={`${style.siteSelectionHeader} ${style.alignHeader}`}>
          {getAlertDescription()}
        </div>
        <div className={style.alertRow}>
          <div className={style.labelCol}>
            {" "}
            {reminderType === "TIMESHEET_REVIEW_APPROVE_REMINDER"
              ? "Enable Alert"
              : "Notification"}
          </div>
          <CommonSwitch
            defaultChecked={false}
            checked={expirationNotificationEnabled}
            label={expirationNotificationEnabled ? "Yes" : "No"}
            onChange={handleExpirationSwitchChange}
          />
        </div>
        {expirationNotificationEnabled && (
          <div className={style.subRow}>
            <div className={style.label}>Email Notification</div>
            <div className={style.templateLink}>
              <span>Default Template In Use</span>
              <button
                className={style.viewTemplate}
                onClick={() => handleViewTemplate("expiration")}
              >
                VIEW TEMPLATE
              </button>
            </div>
          </div>
        )}
      </div>

      <div
        className={style.buttonContainer}
        style={{
          position: "absolute",
          bottom: "20px",
          right: "25px",
        }}
      >
        {/* <button className={style.saveProgress}>SAVE IN PROGRESS</button> */}
        <button className={style.save} onClick={handleSave}>
          SAVE
        </button>
      </div>

      {showSaveConfirmation && (
        <AddSiteSelectionPopup
          show={showSaveConfirmation}
          handleClose={() => {
            setShowSaveConfirmation(false);
            // Don't close the modal or navigate when closing with the X
          }}
          handleSelectedSite={() => {
            setShowSaveConfirmation(false);
            if (inModal) {
              navigate("/alerts-notifications?view=alertsTable", {
                state: {
                  reminderType: reminderType || "TIMESHEET_CREATION_REMINDER",
                },
              });
            } else {
              navigate("/alerts-notifications/multiSiteAlerts", {
                state: {
                  reminderType: reminderType || "TIMESHEET_CREATION_REMINDER",
                },
              });
            }
            if (setOpenAlert) {
              setOpenAlert(false);
            }
          }}
          reminderType={reminderType}
          isSaveConfirmation={true}
          selectedSite={selectedSite}
        />
      )}

      {emailTemplateData.isOpen && (
        <EmailTemplates
          templateName={templateName}
          getShowMailTemplate={handleTemplateClose}
          templateType={emailTemplateData.type}
          reminderIndex={emailTemplateData.reminderIndex}
          reminderData={
            data?.reminderData?.[0] || {
              reminderDetails: reminders.map((r) => ({
                ...r,
                templateBody: r.templateBody || "Default template",
                customTemplate: r.customTemplate || false,
              })),
              alertDetails: {
                ...alertDetails,
                templateBody: alertDetails.templateBody || "Default template",
                customTemplate: alertDetails.customTemplate || false,
              },
              expirationNotificationDetails: {
                ...expirationNotificationTemplate,
                templateBody:
                  expirationNotificationTemplate.templateBody ||
                  "Default template",
                customTemplate:
                  expirationNotificationTemplate.customTemplate || false,
              },
              pastDueNotificationDetails: {
                templateBody:
                  data?.reminderData?.[0]?.pastDueNotificationDetails
                    ?.templateBody || "Default template",
                customTemplate:
                  data?.reminderData?.[0]?.pastDueNotificationDetails
                    ?.customTemplate || false,
              },
            }
          }
          reminderType={reminderType}
          buttonText={getButtonText()}
          onSave={handleTemplateSave}
          templateBody={emailTemplateData.currentTemplateBody}
          isCustomTemplate={emailTemplateData.isCustomTemplate}
          templateData={getTemplateHeaderData()}
          toEmail={getToEmail()}
          subject={getSubject()}
        />
      )}
    </div>
  );
};

export default TimesheetAlerts;
